import { Badge, Box, Flex, Loader, Text } from "@dehub/theme";
import { StarIcon } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { WarehouseStaffApi } from "@/apis";
import type { IWarehouseStaffRole, IWarehouseStaffScope } from "@/apis/warehouseStaff";
import HttpHandler from "@/utils/http-handler";
import { message } from "@/utils/message";
import { WAREHOUSE_STAFF_ROLES, getRoleMeta, getRoleLabel } from "../constants";

interface IUserWarehouseRow {
  id: string;
  refCode: string;
  warehouseName: string;
  staffRole: IWarehouseStaffRole;
  scope: IWarehouseStaffScope;
  isPrimary: boolean;
  canManageStaff: boolean;
}

interface IResponse {
  warehouses: IUserWarehouseRow[];
}

interface Props {
  userId: string;
  title?: string;
}

export function UserWarehouseChips({ userId, title }: Props) {
  const { t } = useTranslation("staff");
  const [rows, setRows] = useState<IUserWarehouseRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    WarehouseStaffApi.byUser(userId)
      .then((res) => {
        if (cancelled) return;
        setRows(((res as IResponse) || {}).warehouses ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        message.error((err as HttpHandler)?.message ?? t("msg.fetchError"));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [userId, t]);

  const grouped = useMemo(() => {
    const map = new Map<IWarehouseStaffRole, IUserWarehouseRow[]>();
    rows.forEach((row) => {
      const list = map.get(row.staffRole) ?? [];
      list.push(row);
      map.set(row.staffRole, list);
    });
    return WAREHOUSE_STAFF_ROLES
      .map((r) => ({ role: r, rows: map.get(r.code) ?? [] }))
      .filter((g) => g.rows.length > 0);
  }, [rows]);

  return (
    <Box>
      <Text fw={600} size="sm" mb="sm">{title ?? t("warehouse.chipsTitle")}</Text>

      {loading ? (
        <Flex justify="center" py="md"><Loader size="sm" /></Flex>
      ) : rows.length === 0 ? (
        <Text c="dimmed" size="sm">
          {t("warehouse.chipsEmpty")}
        </Text>
      ) : (
        <Flex direction="column" gap="md">
          {grouped.map(({ role, rows: roleRows }) => (
            <Box key={role.code}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs">
                {getRoleLabel(t, role.code)}
              </Text>
              <Flex gap="xs" wrap="wrap">
                {roleRows.map((row) => {
                  const meta = getRoleMeta(row.staffRole);
                  return (
                    <Badge
                      key={row.id}
                      component={Link}
                      to={`/our-warehouse/${row.id}`}
                      color={meta?.color ?? "gray"}
                      variant="light"
                      radius="sm"
                      size="lg"
                      style={{ cursor: "pointer", textTransform: "none" }}
                      leftSection={row.isPrimary ? <StarIcon size={12} weight="fill" /> : null}>
                      {row.refCode}
                      {row.warehouseName ? ` - ${row.warehouseName}` : ""}
                      {row.isPrimary ? ` (${t("warehouse.chipsPrimary")})` : ""}
                    </Badge>
                  );
                })}
              </Flex>
            </Box>
          ))}
        </Flex>
      )}
    </Box>
  );
}
